/**
 * Client-side table sorting (CC_05 Block 4). A tiny, dependency-free comparator
 * plus a React hook that toggles asc → desc → asc on repeated header clicks.
 * Pure and unit-tested; the tables (files, library, later lists) share it so
 * "click a column header to sort" behaves identically everywhere.
 */

import { useState } from "react";

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string | null;
  dir: SortDir;
}

/**
 * Compare two arbitrary cell values for sorting. Nullish sinks to the bottom
 * (regardless of direction); numbers compare numerically; everything else
 * compares as a locale-aware, case-insensitive string. ISO date strings sort
 * correctly as strings, so no special-casing is needed.
 */
export function compareValues(a: unknown, b: unknown, dir: SortDir): number {
  const an = a === null || a === undefined || a === "";
  const bn = b === null || b === undefined || b === "";
  if (an && bn) {
    return 0;
  }
  if (an) {
    return 1; // missing values always last
  }
  if (bn) {
    return -1;
  }
  let cmp: number;
  if (typeof a === "number" && typeof b === "number") {
    cmp = a - b;
  } else {
    cmp = String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
  return dir === "asc" ? cmp : -cmp;
}

/**
 * Sort a copy of `rows` by the value the accessor returns for `key`. When no key
 * is active the original order is preserved (returns a shallow copy).
 */
export function sortRows<T>(
  rows: readonly T[],
  sort: SortState,
  accessor: (row: T, key: string) => unknown,
): T[] {
  if (!sort.key) {
    return [...rows];
  }
  const key = sort.key;
  // Stable sort: decorate with the original index and use it as a tiebreak.
  return rows
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      const c = compareValues(
        accessor(x.row, key),
        accessor(y.row, key),
        sort.dir,
      );
      return c !== 0 ? c : x.i - y.i;
    })
    .map((d) => d.row);
}

/** React hook holding the current sort and a header-click toggler. */
export function useTableSort(initial: SortState = { key: null, dir: "asc" }) {
  const [sort, setSort] = useState<SortState>(initial);
  const toggle = (key: string) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  /** The arrow indicator for a header, or "" when that column isn't active. */
  const indicator = (key: string) =>
    sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  return { sort, toggle, indicator };
}
